import {
  ComponentInformationModal,
  LicenseConflictModal,
  LicenseInformationModal,
  PolicyConflictModal,
  SecurityInformationModal,
  VexProgressChip,
  VexSecuritiesBadge,
  VulnerabilityDetailModal,
} from "@components/UI";
import ComponentConflictStatusGuideModal from "@components/UI/ComponentConflictStatusGuideModal";
import { COLOR_CONFLICT_FLAG } from "@constants/index";
import { DEFAULT_ITEMS_PER_PAGE, ITEMS_PER_PAGE_KEY } from "@constants/table";
import {
  buildComponentVexProgressMap,
  buildSnapshotVexProgressMap,
  computeVexProgress,
  hasFrozenSnapshotParentContext,
  mergeVexListByCve,
  sumVexProgress,
  resolveRowVexSourceContexts,
  resolveSnapshotComponentSeq,
  VEX_SNAPSHOT_SOURCE,
  vexComponentKey,
  vexContextKey,
} from "@constants/vex";
import { fetchVexList, fetchVexSnapshot, vexSnapshotQueryKey } from "@apis/vex";
import queryKeys from "@configs/react-query/query-key";
import { useClientConfig } from "@contexts/ClientConfigContext";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import useVulnerabilityPolicy from "@hooks/useVulnerabilityPolicy";
import { displayValueInRange, getBackgroundColor, toKebabCase } from "@utils/common";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Children, cloneElement, isValidElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { useTheme } from "@hooks/useTheme";
import {
  getComponentLicenseConflict,
  getLicensePolicyResult,
  getLicensePolicyRule,
  updateComponentConflictFlag,
} from "../../utils/common";
import CustomInput from "../Forms/CustomInput";
import { SecurityIcon, WarningIconSharp } from "../Icons";
import { VulnerabilityBadge } from "../UI/Badge";
import CustomSelect from "../UI/CustomSelect";
import TableWithPagination from "./TableWithPagination";

const hasComponentSecurities = (row) => {
  const securities = row?.securities;
  if (securities == null || securities === "" || securities === "[]") {
    return false;
  }
  if (Array.isArray(securities)) {
    return securities.length > 0;
  }
  try {
    const parsed = JSON.parse(securities);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
};

const SecuritiesCell = ({ onClick, colors, vexProgress, showVexBadge, dataTestId }) => (
  <div
    className="cursor-pointer inline-flex flex-col items-center justify-center gap-0.5"
    onClick={onClick}
    data-testid={dataTestId}
  >
    <SecurityIcon size={20} className={`${colors.textActive} flex-shrink-0`} />
    {showVexBadge && (
      <VexSecuritiesBadge progress={vexProgress} data-testid={`${dataTestId}-vex-badge`} />
    )}
  </div>
);
// Reusable checkbox component to reduce duplication
const EditableCheckbox = ({ checked, onChange, colors }) => (
  <div className="flex items-center justify-center">
    <input
      type="checkbox"
      className="sr-only"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      tabIndex={-1}
    />
    <div
      className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200
        ${checked
          ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
          : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
        }`}
      onClick={() => onChange(!checked)}
    >
      {checked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </div>
  </div>
);

// Reusable display checkbox component
const DisplayCheckbox = ({ checked, colors }) => (
  <div className="flex items-center justify-center">
    <span className={`text-sm ${colors.textPrimary}`}>{checked ? "✓" : "---"}</span>
  </div>
);

const ComponentTable = ({
  data = [],
  licensePolicyData = [],
  className = "",
  onDataChange = null,
  maxRowsPerPage = 10,
  showPagination = true,
  isEditingAllowed = false,
  seq = "",
  isFossIDDependency = false,
  children,
  scrollable = false,
  /** Fixed row height (px) forwarded to TableWithPagination; also drops component cell min-height. */
  rowHeight = null,
  showIcon = true,
  validationMode = null,
  allowVexReview = false,
  readOnlyVexReview = false,
  /** Frozen VEX snapshot for export/merged views. */
  frozenVexSnapshot = null,
  editableFrozenVexSnapshot = false,
  /** { scanToolCd, verificationSeq } — required for live VEX review API */
  vexContext = null,
  /**
   * (row) => vexContext | null — per-row live VEX context for tables whose rows
   * come from different verifications (e.g. SBOM merge). Takes precedence over
   * `vexContext` for row progress and the security modal.
   */
  resolveRowVexContext = null,
  /** When true, show a checkbox to filter rows that have security (shield) data */
  showShieldFilter = false,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const clientConfig = useClientConfig();
  const [tableData, setTableData] = useState(data);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterByShieldOnly, setFilterByShieldOnly] = useState(false);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(
    ITEMS_PER_PAGE_KEY.COMPONENT_TABLE_LIST,
    DEFAULT_ITEMS_PER_PAGE,
  );

  // Add state for security modal
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [selectedSecurityData, setSelectedSecurityData] = useState(null);
  const [selectedVexComponent, setSelectedVexComponent] = useState(null);
  const [selectedFrozenVexComponentSeq, setSelectedFrozenVexComponentSeq] = useState(null);
  const [selectedRowVexContext, setSelectedRowVexContext] = useState(null);
  const [selectedRowVexContexts, setSelectedRowVexContexts] = useState([]);

  const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));
  const hasFrozenSnapshot = Boolean(frozenVexSnapshot?.source);
  const hasFrozenParentContext = hasFrozenSnapshotParentContext(frozenVexSnapshot);
  const hasSingleVexContext = Boolean(
    allowVexReview && vexContext?.scanToolCd && vexContext?.verificationSeq != null,
  );
  const hasRowVexContexts = Boolean(
    allowVexReview && resolveRowVexContext && !hasFrozenSnapshot,
  );

  /** Distinct source verifications across rows — one GET /vex per verification */
  const rowVexContexts = useMemo(() => {
    if (!hasRowVexContexts) return [];
    const byKey = new Map();
    (data || []).forEach((row) => {
      resolveRowVexSourceContexts(row, resolveRowVexContext).forEach((rowContext) => {
        const contextKey = vexContextKey(rowContext);
        if (contextKey && !byKey.has(contextKey)) byKey.set(contextKey, rowContext);
      });
    });
    return Array.from(byKey.values());
  }, [hasRowVexContexts, data, resolveRowVexContext]);

  const showVexSecuritiesBadge = Boolean(
    isVexOn &&
    ((hasFrozenSnapshot && hasFrozenParentContext) || hasSingleVexContext || rowVexContexts.length > 0),
  );

  const vexListQuery = useQuery({
    queryKey: [queryKeys.vexList, vexContext?.scanToolCd, vexContext?.verificationSeq, "", ""],
    queryFn: () =>
      fetchVexList({
        scanToolCd: vexContext.scanToolCd,
        verificationSeq: vexContext.verificationSeq,
      }),
    enabled: showVexSecuritiesBadge && !hasFrozenSnapshot && hasSingleVexContext,
  });

  const rowVexListQueries = useQueries({
    queries: rowVexContexts.map((rowContext) => ({
      queryKey: [queryKeys.vexList, rowContext.scanToolCd, rowContext.verificationSeq, "", ""],
      queryFn: () =>
        fetchVexList({
          scanToolCd: rowContext.scanToolCd,
          verificationSeq: rowContext.verificationSeq,
        }),
      enabled: showVexSecuritiesBadge,
    })),
  });

  const frozenVexQuery = useQuery({
    queryKey: vexSnapshotQueryKey(frozenVexSnapshot),
    queryFn: () => fetchVexSnapshot(frozenVexSnapshot),
    enabled: showVexSecuritiesBadge && hasFrozenSnapshot && hasFrozenParentContext,
  });

  // Verification-report VEX syncs to live — only editable when source verification is DONE.
  const effectiveEditableFrozenVexSnapshot = Boolean(
    editableFrozenVexSnapshot &&
    (frozenVexSnapshot?.source !== VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT ||
      frozenVexQuery.data?.verificationStatusCd === "DONE"),
  );

  const componentVexProgressMap = useMemo(() => {
    if (hasFrozenSnapshot) {
      return buildSnapshotVexProgressMap(frozenVexQuery.data?.list);
    }
    return buildComponentVexProgressMap(vexListQuery.data?.list);
  }, [hasFrozenSnapshot, frozenVexQuery.data?.list, vexListQuery.data?.list]);

  const getLiveRowVexList = (row) => {
    if (!hasRowVexContexts) return [];
    const key = vexComponentKey(
      row?.componentName || row?.component || "",
      row?.componentVersion || "",
    );
    const liveVex = [];
    resolveRowVexSourceContexts(row, resolveRowVexContext).forEach((rowContext) => {
      const contextKey = vexContextKey(rowContext);
      const contextIndex = rowVexContexts.findIndex(
        (ctx) => vexContextKey(ctx) === contextKey,
      );
      if (contextIndex < 0) return;
      const list = rowVexListQueries[contextIndex]?.data?.list;
      if (!Array.isArray(list)) return;
      list.forEach((vex) => {
        if (vexComponentKey(vex?.componentName, vex?.componentVersion) === key) {
          liveVex.push(vex);
        }
      });
    });
    return liveVex;
  };

  const getRowVexProgress = (row) => {
    if (hasFrozenSnapshot) {
      const componentSeq = resolveSnapshotComponentSeq(row, frozenVexSnapshot.source);
      // Prefer row PK only — do not fall back to name|version when seq exists,
      // otherwise rows that differ only by disclose/open share the wrong VEX.
      if (componentSeq != null) {
        return componentVexProgressMap.get(String(componentSeq)) || null;
      }
      const identityKey = vexComponentKey(row?.componentName || row?.component || "", row?.componentVersion || "");
      if (!identityKey) return null;
      const frozenProgress = componentVexProgressMap.get(identityKey) || null;
      if (frozenProgress) return frozenProgress;
    }
    const key = vexComponentKey(row?.componentName || row?.component || "", row?.componentVersion || "");
    if (hasRowVexContexts) {
      const mergedVex = getLiveRowVexList(row);
      if (mergedVex.length === 0) return null;
      return computeVexProgress(mergeVexListByCve(mergedVex));
    }
    return componentVexProgressMap.get(key) || null;
  };

  const openSecurityModal = (securities, row) => {
    setSelectedSecurityData(securities);
    setSelectedVexComponent({
      componentName: row?.componentName || row?.component || "",
      componentVersion: row?.componentVersion || "",
    });
    setSelectedFrozenVexComponentSeq(
      hasFrozenSnapshot ? resolveSnapshotComponentSeq(row, frozenVexSnapshot.source) : null,
    );
    setSelectedRowVexContext(hasRowVexContexts ? resolveRowVexContext(row) : null);
    setSelectedRowVexContexts(
      hasRowVexContexts ? resolveRowVexSourceContexts(row, resolveRowVexContext) : [],
    );
    setIsSecurityModalOpen(true);
  };
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
  const [selectedComponentData, setSelectedComponentData] = useState(null);
  // Add state for conflict modals
  const [isLicenseConflictModalOpen, setIsLicenseConflictModalOpen] = useState(false);
  const [isPolicyConflictModalOpen, setIsPolicyConflictModalOpen] = useState(false);
  const [selectedConflictData, setSelectedConflictData] = useState([]);
  const [selectedLicensePolicyResult, setSelectedLicensePolicyResult] = useState(null);
  // Add state for license information modal
  const [isLicenseInformationModalOpen, setIsLicenseInformationModalOpen] = useState(false);
  const [selectedLicenseInformationData, setSelectedLicenseInformationData] = useState(null);
  const [isVulnerabilityModalOpen, setIsVulnerabilityModalOpen] = useState(false);
  const [selectedVulnerabilityData, setSelectedVulnerabilityData] = useState(null);
  const { matchTypeList, fetchSearchInfo } = useSearchInfo();
  const { vulnerabilityLevel } = useVulnerabilityPolicy();

  const displayData = useMemo(() => {
    return tableData.length > 0 ? tableData : data;
  }, [data, tableData]);

  const tableViewData = useMemo(() => {
    if (!showShieldFilter || !filterByShieldOnly) return displayData;
    return displayData.filter(hasComponentSecurities);
  }, [displayData, showShieldFilter, filterByShieldOnly]);

  const tableVexProgress = useMemo(() => {
    if (!showVexSecuritiesBadge) return null;

    // Frozen (Verification Report / Notice / merged SBOM saved / product merge): GET /vex/{source} → progress
    if (hasFrozenSnapshot && hasFrozenParentContext) {
      return frozenVexQuery.data?.progress ?? null;
    }
    // Live single verification (Clarity / SBOM / FossID / ClarityAIR): GET /vex → progress
    if (!hasFrozenSnapshot && hasSingleVexContext) {
      return vexListQuery.data?.progress ?? null;
    }

    // Multi-verification merge (Add/Detail SBOM merge live): no single API total —
    // sum per-row computeVexProgress (full status breakdown).
    if (!tableViewData.length) return null;
    const rowProgresses = tableViewData
      .map((row) => getRowVexProgress(row))
      .filter((progress) => progress && progress.total > 0);
    return sumVexProgress(rowProgresses);
  }, [
    showVexSecuritiesBadge,
    tableViewData,
    componentVexProgressMap,
    hasFrozenSnapshot,
    hasFrozenParentContext,
    hasSingleVexContext,
    frozenVexQuery.data?.list,
    frozenVexQuery.data?.progress,
    vexListQuery.data?.list,
    vexListQuery.data?.progress,
    hasRowVexContexts,
    rowVexListQueries,
    frozenVexSnapshot?.source,
  ]);

  useEffect(() => {
    fetchSearchInfo();
  }, []);

  // Initialize table data with conflict flags when data changes
  useEffect(() => {
    if (data?.length === 0 && tableData.length > 0) {
      setTableData(data);
    } else if (data?.length > 0 && licensePolicyData) {
      // Use row's licensePolicy if available, otherwise use licensePolicyData from props
      const processedData = data.map((component) => {
        const policyToUse = component.licensePolicy || licensePolicyData;
        return updateComponentConflictFlag(data, component, policyToUse);
      });
      setTableData(processedData);
    }
  }, [data, tableData.length]);

  // Memoize conflict results instead of using useEffect
  const conflictResults = useMemo(() => {
    if (!licensePolicyData || !tableViewData.length) return [];

    return tableViewData.map((row) => {
      // Use row's licensePolicy if available, otherwise use licensePolicyData from props
      const policyToUse = row.licensePolicy || licensePolicyData;
      const licensePolicyResult = getLicensePolicyResult(policyToUse, row.licenseAttribute);
      let { policyConflictColor, policyStepColor, policyStep, conflictDetails } = licensePolicyResult;

      // Check if there's a specific rule that applies
      const licensePolicyRule = getLicensePolicyRule(policyToUse?.licensePolicyRules, row);
      if (licensePolicyRule != null) {
        policyConflictColor = "white";
        policyStepColor = licensePolicyRule.policyStepColor;
        policyStep = licensePolicyRule.policyStep;

        if (licensePolicyRule.licenseUseApprovalCd === "DISAPPROVAL") {
          policyConflictColor = COLOR_CONFLICT_FLAG.POLICY_CONFLICT;
        }
      }

      return {
        policyConflictColor,
        policyStepColor,
        policyStep,
        conflictDetails,
      };
    });
  }, [tableViewData, licensePolicyData]);

  const componentConflictResults = useMemo(() => {
    if (!displayData.length) return [];

    return displayData.map((row) => {
      return getComponentLicenseConflict(displayData, row);
    });
  }, [displayData]);

  // Update conflict status when data changes
  const updateConflictStatus = (rowIndex, field, value) => {
    if (!licensePolicyData) return;

    // Create a deep copy of current data
    let updatedData = [...(tableData.length > 0 ? tableData : data)];

    // Get the component that already contains any previous changes
    const component = { ...updatedData[rowIndex] };

    // Ensure all required fields have default values
    component.modificationFlag = component.modificationFlag || "N";
    component.sourceOpenFlag = component.sourceOpenFlag || "N";
    component.noticeFlag = component.noticeFlag || "N";
    component.patentFlag = component.patentFlag || "N";
    component.matchTypeCd = component.matchTypeCd || "";

    // Update the specific field based on what changed
    if (field === "usageType") {
      component.matchTypeCd = value;
      // Clear remark if changing from ETC to another type
      if (component.matchTypeCd !== "ETC") {
        component.matchTypeRemark = "";
      }
    } else if (field === "modification") {
      component.modificationFlag = value === "Y" ? "Y" : "N";
    } else if (field === "disclose") {
      component.sourceOpenFlag = value === "Y" ? "Y" : "N";
    } else if (field === "notice") {
      component.noticeFlag = value === "Y" ? "Y" : "N";
    } else if (field === "patent") {
      component.patentFlag = value === "Y" ? "Y" : "N";
    }

    // Use row's licensePolicy if available, otherwise use licensePolicyData from props
    const policyToUse = component.licensePolicy || licensePolicyData;
    // Update the component with conflict flag
    const updatedComponent = updateComponentConflictFlag(updatedData, component, policyToUse);

    // Update tableData with the changed component
    updatedData[rowIndex] = updatedComponent;
    setTableData(updatedData);

    // The conflict results will be automatically recalculated by useMemo
    // when tableData changes, so no need to manually update them here

    // Call onDataChange if provided
    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  // Helper functions for rendering conflicts
  const renderLicensePolicyConflict = (dataToRender, licensePolicy) => {
    if (!licensePolicy || !dataToRender.length) return [];

    return dataToRender.map((row) => {
      const licensePolicyResult = getLicensePolicyResult(licensePolicy, row.licenseAttribute);
      let { policyConflictColor, policyStepColor, policyStep, conflictDetails } = licensePolicyResult;

      // Check if there's a specific rule that applies
      const licensePolicyRule = getLicensePolicyRule(licensePolicy?.licensePolicyRules, row);
      if (licensePolicyRule != null) {
        policyConflictColor = "white";
        policyStepColor = licensePolicyRule.policyStepColor;
        policyStep = licensePolicyRule.policyStep;

        if (licensePolicyRule.licenseUseApprovalCd === "DISAPPROVAL") {
          policyConflictColor = "darkred";
        }
      }

      return {
        policyConflictColor,
        policyStepColor,
        policyStep,
        conflictDetails,
      };
    });
  };

  const renderComponentConflict = (dataToRender) => {
    if (!dataToRender.length) return [];

    return dataToRender.map((row) => {
      return getComponentLicenseConflict(dataToRender, row);
    });
  };

  const handleShieldFilterChange = (checked) => {
    setFilterByShieldOnly(checked);
    setCurrentPage(1);
  };

  // Calculate pagination
  const totalData = tableViewData.length;
  const totalPages = Math.ceil(totalData / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = tableViewData.slice(startIndex, endIndex);

  // Usage type options with translations
  const usageTypeOptions = [
    {
      value: "N/A",
      label: t("common.usage-type.na"),
    },
  ]
    .concat(matchTypeList)
    .map((item) => ({
      value: item.value,
      label:
        item.value === "N/A"
          ? t("common.usage-type.na")
          : item.label === "etc"
            ? t("common.usage-type.etc")
            : t(`common.usage-type.${toKebabCase(item.label)}`),
    }));

  const handleCheckboxChange = (rowId, field, value) => {
    const updatedData = (tableData.length > 0 ? tableData : data).map((row) =>
      row[seq] === rowId ? { ...row, [field]: value } : row,
    );
    setTableData(updatedData);

    // Find the row index and update conflict status
    const rowIndex = updatedData.findIndex((item) => item[seq] === rowId);
    if (rowIndex !== -1) {
      // Map field name to the correct field for updateConflictStatus
      let conflictField = field;
      if (field === "sourceOpenFlag") conflictField = "disclose";
      else if (field === "modificationFlag") conflictField = "modification";
      else if (field === "noticeFlag") conflictField = "notice";
      else if (field === "patentFlag") conflictField = "patent";

      updateConflictStatus(rowIndex, conflictField, value);
    }

    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  const handleUsageTypeChange = (rowId, selectedOption) => {
    const updatedData = (tableData.length > 0 ? tableData : data).map((row) => {
      if (row[seq] === rowId) {
        // If changing from ETC to another type, clear the remark
        const updatedRow = { ...row, matchTypeCd: selectedOption.value };
        if (row.matchTypeCd === "ETC" && selectedOption.value !== "ETC") {
          updatedRow.matchTypeRemark = "";
        }
        return updatedRow;
      }
      return row;
    });
    setTableData(updatedData);

    // Find the row index and update conflict status
    const rowIndex = updatedData.findIndex((item) => item[seq] === rowId);
    if (rowIndex !== -1) {
      updateConflictStatus(rowIndex, "usageType", selectedOption.value);
    }

    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  const handleRemarkChange = (rowId, remark) => {
    const updatedData = (tableData.length > 0 ? tableData : data).map((row) =>
      row[seq] === rowId ? { ...row, matchTypeRemark: remark } : row,
    );
    setTableData(updatedData);

    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  // Helper to create color grid for conflict status
  const createColorGrid = (row) => {
    // Use row's licensePolicy if available, otherwise use licensePolicyData from props
    let licensePolicy = row.licensePolicy || licensePolicyData;
    let previewComponents = data;

    // Early return if no license policy data
    if (!licensePolicy || !licensePolicy.licensePolicyRules) {
      const itemStyle = { border: "none" };
      return {
        policyConflict: { ...itemStyle, backgroundColor: COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT },
        componentConflict: { ...itemStyle, backgroundColor: COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT },
        licensePolicyResult: { policyConflictColor: COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT, conflictDetails: [] },
        componentConflictResult: { conflict: false, conflictComponents: [] },
      };
    }

    let licensePolicyResult = getLicensePolicyResult(licensePolicy, row.licenseAttribute);
    let policyConflictColor = licensePolicyResult.policyConflictColor;
    let licensePolicyRule = getLicensePolicyRule(licensePolicy.licensePolicyRules, row);

    if (licensePolicyRule != null) {
      policyConflictColor = COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
      if (licensePolicyRule.licenseUseApprovalCd === "DISAPPROVAL") {
        policyConflictColor = "darkred";
      }
    }

    let componentConflictResult = getComponentLicenseConflict(previewComponents, row);

    const itemStyle = {
      border: "none",
    };

    const policyConflict = {
      ...itemStyle,
      backgroundColor: policyConflictColor,
    };

    const componentConflict = {
      ...itemStyle,
      backgroundColor: componentConflictResult.conflict
        ? COLOR_CONFLICT_FLAG.LICENSE_CONFLICT
        : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT,
    };

    return {
      policyConflict,
      componentConflict,
      licensePolicyResult,
      componentConflictResult,
    };
  };

  const getVulnerabilityDataMutation = useMutationData(
    (levelCode) => `/vulnerability_policy/level_code/${levelCode}`,
    { method: "GET" },
    {
      onSuccess: (response, levelCode) => {
        setSelectedVulnerabilityData({ ...response, vulnerabilityLevel: levelCode });
        setIsVulnerabilityModalOpen(true);
      },
      onError: (error) => {
        console.error("Error:", error);
      },
    },
  );

  const handleVulnerabilityClick = async (vulnerabilityLevel) => {
    if (vulnerabilityLevel) {
      await getVulnerabilityDataMutation.mutateAsync(vulnerabilityLevel);
    }
  };

  // Generic checkbox renderer - conditional editing based on isEditingAllowed
  const renderCheckbox = (row, field) => {
    const checked = row[field] === "Y";

    if (isEditingAllowed) {
      return (
        <EditableCheckbox
          checked={checked}
          onChange={(newValue) => handleCheckboxChange(row[seq], field, newValue ? "Y" : "N")}
          colors={colors}
        />
      );
    } else {
      return <DisplayCheckbox checked={checked} colors={colors} />;
    }
  };

  const renderCell = (row, columnKey, index) => {
    switch (columnKey) {
      case "conflict":
        // Render the color grid for conflict status
        const { policyConflict, componentConflict, licensePolicyResult, componentConflictResult } =
          createColorGrid(row);

        const handleLicenseConflictClick = () => {
          if (
            componentConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT &&
            componentConflictResult?.conflictComponents
          ) {
            setSelectedConflictData(componentConflictResult.conflictComponents);
            setIsLicenseConflictModalOpen(true);
          }
        };

        return (
          <div className="flex items-center justify-start w-full">
            <div className="flex items-center gap-x-0.5">
              <div
                className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-l-sm ${policyConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                style={{ backgroundColor: policyConflict.backgroundColor, borderRight: "none" }}
                onClick={() => {
                  if (policyConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT) {
                    setSelectedLicensePolicyResult(licensePolicyResult);
                    setIsPolicyConflictModalOpen(true);
                  }
                }}
              >
                <span
                  className={
                    policyConflict.backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT
                      ? colors.textConflictUndefined
                      : colors.textOnPrimary
                  }
                >
                  P
                </span>
              </div>
              <div
                className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-r-sm transition-opacity ${componentConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT &&
                    componentConflictResult?.conflictComponents
                    ? "cursor-pointer hover:opacity-80"
                    : "cursor-default"
                  }
                `}
                style={{ backgroundColor: componentConflict.backgroundColor, borderLeft: "none" }}
                onClick={
                  componentConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT &&
                    componentConflictResult?.conflictComponents
                    ? handleLicenseConflictClick
                    : undefined
                }
              >
                <span
                  className={
                    componentConflict.backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT
                      ? colors.textConflictUndefined
                      : colors.textOnPrimary
                  }
                >
                  L
                </span>
              </div>
            </div>
          </div>
        );

      case "component":
        const componentVersion = row.componentVersion || "";
        const licenseIdentifier = row.componentLicense || row.licenseIdentifier || row.licenseName;
        const isValidLicense = licenseIdentifier != null && licenseIdentifier.toUpperCase() !== "N/A";

        const isVersionLong = componentVersion.length > 15; // Threshold for truncation

        // Check if component name is missing when validationMode is "export"
        const componentName = row.componentName || row.component || "";
        const isComponentNameEmpty = !componentName || componentName.trim() === "";
        const isMissingName = validationMode === "export" && isComponentNameEmpty;
        const tooltipId = `warning-tooltip-${row[seq] || index}`;

        const handleLicenseClick = (e) => {
          e.stopPropagation();
          if (isValidLicense) {
            setSelectedLicenseInformationData(licenseIdentifier);
            setIsLicenseInformationModalOpen(true);
          }
        };

        const handleComponentClick = (e) => {
          e.stopPropagation();
          // Prevent opening modal (and its API call) when component name is empty.
          if (isComponentNameEmpty) return;
          setSelectedComponentData(row);
          setIsComponentModalOpen(true);
        };

        return (
          <div className={`flex items-center gap-1.5 w-full min-w-0 ${rowHeight ? "" : "min-h-[48px]"}`}>
            <div className="flex-1 min-w-0 overflow-hidden">
              <div
                className={`text-sm font-medium ${colors.textPrimary} transition-colors truncate ${isComponentNameEmpty ? "cursor-default" : `cursor-pointer ${colors.textHoverActive}`
                  }`}
                onClick={handleComponentClick}
                title={`${componentName || ""} (${componentVersion})`}
              >
                <span className={`${colors.textPrimary}`}>{componentName}</span>
                <span className={colors.textMuted}>
                  {` (`}
                  {isVersionLong ? (
                    <span className="truncate max-w-[120px] inline-block align-bottom" title={componentVersion}>
                      {componentVersion}
                    </span>
                  ) : (
                    componentVersion
                  )}
                  {`)`}
                </span>
              </div>
              <div
                className={`mt-0.5 text-xs truncate ${isValidLicense ? `cursor-pointer ${colors.textActive} ${colors.textHoverActive} transition-colors` : colors.textMuted}`}
                onClick={isValidLicense ? handleLicenseClick : undefined}
                title={isValidLicense ? licenseIdentifier : ""}
              >
                {isValidLicense ? licenseIdentifier : ""}
              </div>
            </div>

            {isMissingName && (
              <>
                <div
                  className="flex items-center justify-center cursor-pointer flex-shrink-0"
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={t("common.component-name-missing-warning")}
                >
                  <WarningIconSharp size="22" />
                </div>
                <Tooltip
                  id={tooltipId}
                  place="right"
                  className={`!text-xs !rounded-lg !px-3 !py-2 !max-w-[300px] !min-w-[180px] !z-50 !break-words ${colors.bgPrimaryDarker} ${colors.textPrimary}`}
                  style={{
                    backgroundColor: "#f3f4f6",
                    color: "#111827",
                    fontSize: "12px",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    maxWidth: "250px",
                    minWidth: "180px",
                    zIndex: 50,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    lineHeight: "1.4",
                  }}
                >
                  {t("common.component-name-missing-warning")}
                </Tooltip>
              </>
            )}
          </div>
        );

      case "obligType":
        const conflictResult = conflictResults[startIndex + index];
        const policyStepColor = conflictResult?.policyStepColor;
        // #FFFFFF = undefined license; #9EA5B3 = undefined obligation type
        const backgroundColor =
          !policyStepColor ||
          policyStepColor === COLOR_CONFLICT_FLAG.UNDEFINED_OBLIGATION ||
          policyStepColor === "white"
            ? COLOR_CONFLICT_FLAG.UNDEFINED_OBLIGATION
            : policyStepColor === COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT
              ? COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT
              : getBackgroundColor(policyStepColor);

        return (
          <div
            className="inline-flex items-center justify-center font-extrabold rounded transition-colors px-1.5 py-0.5 text-xs h-8 w-8"
            style={{
              backgroundColor,
              color: backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_OBLIGATION ? "#000000" : "#FFFFFF",
              border: backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_OBLIGATION ? "1px solid #9CA3AF" : "none",
            }}
          >
            {conflictResult?.policyStep ? displayValueInRange(conflictResult?.policyStep) : ""}
          </div>
        );

      case "usageType":
        if (isEditingAllowed) {
          // Edit mode: show CustomSelect dropdown
          const currentData = tableData.length > 0 ? tableData : data;
          const currentRow = currentData.find((item) => item[seq] === row[seq]) || row;
          const currentValue = usageTypeOptions.find((option) => option.value === (currentRow.matchTypeCd || ""));
          // If no matching option found, create a custom option or use the original value
          const valueToUse = currentValue || { value: "N/A", label: t("common.usage-type.na") };

          return (
            <div className="relative">
              <CustomSelect
                options={usageTypeOptions}
                value={valueToUse}
                onChange={(selectedOption) => handleUsageTypeChange(row[seq] || "", selectedOption)}
                placeholder={t("common.please-select")}
                size="sm"
                className="w-full text-sm font-medium"
                menuPlacement="top"
                menuPosition="fixed"
                isSearchable={false}
                clearable={false}
              />
              {/* Show remark textbox when ETC is selected */}
              {valueToUse.value === "ETC" && (
                <div className="mt-2">
                  <CustomInput
                    type="text"
                    value={currentRow.matchTypeRemark || ""}
                    onChange={(e) => handleRemarkChange(row[seq] || "", e.target.value)}
                    placeholder={t("common.enter-remark")}
                    size="sm"
                    className="w-full"
                  />
                </div>
              )}
            </div>
          );
        } else {
          // Display mode: show text
          const currentData = tableData.length > 0 ? tableData : data;
          const currentRow = currentData.find((item) => item[seq] === row[seq]) || row;
          const currentOption = usageTypeOptions.find((option) => option.value === (currentRow.matchTypeCd || ""));

          return (
            <div>
              <span className={`text-sm ${colors.textPrimary}`}>
                {currentOption ? currentOption.label : t("common.usage-type.na")}
              </span>
              {/* Show remark when ETC is selected in view mode */}
              {currentRow.matchTypeCd === "ETC" && currentRow.matchTypeRemark && (
                <div className="mt-1">
                  <span className={`text-xs ${colors.textMuted} block`}>{currentRow.matchTypeRemark}</span>
                </div>
              )}
            </div>
          );
        }

      case "disclose":
        return renderCheckbox(row, "sourceOpenFlag");

      case "modification":
        return renderCheckbox(row, "modificationFlag");

      case "notice":
        return renderCheckbox(row, "noticeFlag");

      case "patent":
        return renderCheckbox(row, "patentFlag");

      case "url":
        return (
          <div className="max-w-xs">
            <span className={`text-sm ${colors.textPrimary} break-all`}>{row.url || "---"}</span>
          </div>
        );

      case "maxDepth":
        return <span className={`text-sm ${colors.textPrimary}`}>{row.maxDepth || "---"}</span>;

      case "vulLevel":
        const matchedData = vulnerabilityLevel.find((item) => item.levelCode === row.vulnerabilityLevel);
        if (matchedData) {
          return (
            <VulnerabilityBadge
              level={row.vulnerabilityLevel}
              bgColor={matchedData.vulnerabilityLevel}
              className="font-extrabold cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                handleVulnerabilityClick(row?.vulnerabilityLevel);
              }}
            />
          );
        } else {
          return <span className={`text-sm ${colors.textMuted}`}>---</span>;
        }

      case "severity":
        return row.cvssScore ? (
          <span className={`text-sm ${colors.textPrimary} capitalize`}>{row.cvssScore}</span>
        ) : (
          <span className={`text-sm ${colors.textMuted}`}>---</span>
        );

      case "securities":
        if (row.securities === null || row.securities === "" || row.securities === "[]") {
          return <span className={`text-sm ${colors.textMuted}`}>---</span>;
        }

        // If it's already an array, use it directly
        if (Array.isArray(row.securities) && row.securities.length > 0) {
          return (
            <div className="flex items-center justify-center">
              <SecuritiesCell
                onClick={() => openSecurityModal(row.securities, row)}
                colors={colors}
                vexProgress={getRowVexProgress(row)}
                showVexBadge={showVexSecuritiesBadge}
                dataTestId={`component-table-securities-${row[seq] ?? index}`}
              />
            </div>
          );
        }

        try {
          const parsedSecurities = JSON.parse(row.securities);
          if (Array.isArray(parsedSecurities) && parsedSecurities.length > 0) {
            return (
              <div className="flex items-center justify-center">
                <SecuritiesCell
                  onClick={() => openSecurityModal(parsedSecurities, row)}
                  colors={colors}
                  vexProgress={getRowVexProgress(row)}
                  showVexBadge={showVexSecuritiesBadge}
                  dataTestId={`component-table-securities-${row[seq] ?? index}`}
                />
              </div>
            );
          }
        } catch (e) {
          console.log("error when parsing:", e)
        }

        return <span className={`text-sm ${colors.textMuted}`}>---</span>;

      default:
        return <span className={`text-sm ${colors.textPrimary}`}>{row[columnKey] || ""}</span>;
    }
  };

  // Column configuration for TableWithPagination with translations
  const columns = isFossIDDependency
    ? ["conflict", "component", "obligType", "url", "maxDepth", "severity", "vulLevel", "securities"]
    : [
      "conflict",
      "component",
      "obligType",
      "usageType",
      "disclose",
      "modification",
      "notice",
      "patent",
      "vulLevel",
      "severity",
      "securities",
    ];

  const columnConfig = {
    conflict: {
      headerRender: () => t("common.conflict"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "100px", paddingRight: "0px" },
    },
    component: {
      headerRender: () => (
        <div className="text-left">
          <div className="font-medium truncate" title={t("common.component")}>
            {t("common.component")}
          </div>
          <div className={`text-xs ${colors.textActive} font-normal truncate`} title={t("common.component-license")}>
            {t("common.component-license")}
          </div>
        </div>
      ),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "24%" },
    },
    obligType: {
      headerRender: () => (
        <ComponentConflictStatusGuideModal
          licensePolicyData={licensePolicyData}
          title={t("common.obligation-type")}
          colors={colors}
          t={t}
          showIcon={showIcon}
          showVulnerableLevel={false}
        />
      ),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { textAlign: "center", width: "12%" },
    },
    ...(isFossIDDependency
      ? {
        url: {
          headerRender: () => t("common.url"),
          customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
          style: { width: "25%" },
        },
        maxDepth: {
          headerRender: () => t("common.max-depth"),
          customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
          style: { width: "8%" },
        },
      }
      : {
        usageType: {
          headerRender: () => t("common.usage-type"),
          customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
          style: { width: "20%" },
        },
        disclose: {
          headerRender: () => t("common.disclose"),
          customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
          style: { textAlign: "center", width: "8%" },
        },
        modification: {
          headerRender: () => t("common.modification"),
          customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
          style: { textAlign: "center", width: "10%" },
        },
        notice: {
          headerRender: () => t("common.notice"),
          customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
          style: { textAlign: "center", width: "8%" },
        },
        patent: {
          headerRender: () => t("common.patent"),
          customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
          style: { textAlign: "center", width: "8%" },
        },
      }),
    vulLevel: {
      headerRender: () => (
        <ComponentConflictStatusGuideModal
          title={t("vulnerability-policy.vulnerability-security-code")}
          colors={colors}
          t={t}
          showObligationLevel={false}
        />
      ),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { textAlign: "center", width: "12%" },
    },
    severity: {
      headerRender: () => t("status.severity"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { textAlign: "center", width: "10%" },
    },
    securities: {
      headerRender: () => t("status.securities"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: {
        textAlign: "center",
        width: "10%",
      },
    },
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const headerChildren = useMemo(() => {
    if (!allowVexReview || !vexContext) return children;
    return Children.map(children, (child) => {
      if (!isValidElement(child)) return child;
      return cloneElement(child, {
        showVexProgress: true,
        vexContext,
      });
    });
  }, [allowVexReview, children, vexContext]);

  const activeVexContext = selectedRowVexContext || vexContext;

  const showVexProgressChip = Boolean(
    isVexOn &&
    ((hasFrozenSnapshot && hasFrozenParentContext) ||
      (allowVexReview && vexContext?.scanToolCd && vexContext?.verificationSeq != null) ||
      rowVexContexts.length > 0),
  );
  const shieldFilterControl = showShieldFilter ? (
    <div
      className={`inline-flex shrink-0 cursor-pointer select-none items-center gap-2 ${colors.textSecondary}`}
      data-testid="component-table-shield-filter"
      onClick={() => handleShieldFilterChange(!filterByShieldOnly)}
    >
      <div className="flex items-center justify-center">
        <input
          type="checkbox"
          className="sr-only"
          checked={filterByShieldOnly}
          onChange={(e) => handleShieldFilterChange(e.target.checked)}
          tabIndex={-1}
        />
        <div
          className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200
            ${filterByShieldOnly
              ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
              : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
            }`}
        >
          {filterByShieldOnly && (
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
      {/* <SecurityIcon size={16} className={`${colors.textActive} flex-shrink-0`} aria-hidden="true" /> */}
      <span className="whitespace-nowrap text-sm">{t("common.filter-components-with-security")}</span>
    </div>
  ) : null;

  return (
    <div className={`${colors.bgPrimary} rounded-lg ${className} ${scrollable ? "flex flex-col flex-1 min-h-0" : ""}`} data-testid="component-table">
      {/* Header Section */}
      {headerChildren}

      {/* VEX progress (left) + shield filter (right) */}
      {(showVexProgressChip || shieldFilterControl) && (
        <div className="my-3 flex items-center justify-between gap-4" data-testid="component-table-filters">
          <div className="min-w-0">
            {showVexProgressChip && (
              <VexProgressChip
                progress={tableVexProgress}
                frozenVexSnapshot={
                  !tableVexProgress && hasFrozenSnapshot ? frozenVexSnapshot : null
                }
                scanToolCd={!tableVexProgress ? vexContext?.scanToolCd : undefined}
                verificationSeq={!tableVexProgress ? vexContext?.verificationSeq : undefined}
                labelKey="status.vex-progress-processed"
                titleKey="status.vex-progress-processed-tooltip"
                showInfoTooltip
                data-testid="component-table-vex-progress"
              />
            )}
          </div>
          {shieldFilterControl}
        </div>
      )}

      <TableWithPagination
        dataTestId="component-table-grid"
        columns={columns}
        data={paginatedData}
        colSize={12}
        maxPagesToShow={5}
        maxRowsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalData={totalData}
        columnConfig={columnConfig}
        showPagination={showPagination}
        onItemsPerPageChange={handleItemsPerPageChangeWithReset}
        rowKey={seq}
        tableStyle="default"
        scrollableBody={scrollable}
        rowHeight={rowHeight}
        showItemsPerPageSelector={true}
        useFixedSelect={false}
      />

      {/* Security Information Modal */}
      <SecurityInformationModal
        show={isSecurityModalOpen}
        onClose={() => {
          setIsSecurityModalOpen(false);
          setSelectedSecurityData(null);
          setSelectedVexComponent(null);
          setSelectedFrozenVexComponentSeq(null);
          setSelectedRowVexContext(null);
          setSelectedRowVexContexts([]);
        }}
        data={selectedSecurityData}
        allowVexReview={allowVexReview}
        readOnlyVexReview={readOnlyVexReview}
        frozenVexSnapshot={
          selectedFrozenVexComponentSeq != null ? frozenVexSnapshot : null
        }
        editableFrozenVexSnapshot={effectiveEditableFrozenVexSnapshot}
        frozenVexComponentSeq={selectedFrozenVexComponentSeq}
        scanToolCd={activeVexContext?.scanToolCd}
        verificationSeq={activeVexContext?.verificationSeq}
        rowVexContexts={selectedRowVexContexts}
        componentName={selectedVexComponent?.componentName}
        componentVersion={selectedVexComponent?.componentVersion}
        projectManagerId={activeVexContext?.projectManagerId}
        verificationStatusCd={activeVexContext?.verificationStatusCd}
      />
      <ComponentInformationModal
        show={isComponentModalOpen}
        onClose={() => {
          setIsComponentModalOpen(false);
          setSelectedComponentData(null);
        }}
        componentData={selectedComponentData}
      />
      <LicenseConflictModal
        isOpen={isLicenseConflictModalOpen}
        onClose={() => {
          setIsLicenseConflictModalOpen(false);
          setSelectedConflictData([]);
        }}
        conflictData={selectedConflictData}
      />
      <PolicyConflictModal
        isOpen={isPolicyConflictModalOpen}
        onClose={() => {
          setIsPolicyConflictModalOpen(false);
          setSelectedLicensePolicyResult(null);
        }}
        licensePolicyResult={selectedLicensePolicyResult}
      />
      <LicenseInformationModal
        show={isLicenseInformationModalOpen}
        onClose={() => {
          setIsLicenseInformationModalOpen(false);
          setSelectedLicenseInformationData(null);
        }}
        componentLicense={selectedLicenseInformationData}
      />
      <VulnerabilityDetailModal
        isOpen={isVulnerabilityModalOpen}
        onClose={() => {
          setIsVulnerabilityModalOpen(false);
          setSelectedVulnerabilityData(null);
        }}
        data={selectedVulnerabilityData}
      />
    </div>
  );
};

export default ComponentTable;
